import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { View, StyleSheet, Animated } from "react-native";
import { XIcon } from "@phosphor-icons/react";

import CustomText from "../../../common/general/CustomText/CustomText";
import CustomButton from "../../../common/general/CustomButton/CustomButton";
import AssignmentGradingPanel from "./AssignmentGradingPanel";
import AssessmentGradingPanel from "./AssessmentGradingPanel";
import ProjectGradingPanel from "./ProjectGradingPanel";
import { GradingCenterModalState } from "../types";
import { useGradingCenterSubmissions } from "../../../../contexts/GradingCenterContext";
import {
  GetAssessmentSubmissionsDetailsDocument,
  GetAssessmentSubmissionAttemptHistoryDocument,
  GetGradingCenterDashboardDataDocument,
  UpdateAssessmentSubmissionUpdatedGrade,
  useUpdateAssessmentSubmissionGradeMutation,
  useUpdateStudentAssignmentGradeMutation,
  useGradeProjectSubmissionMutation,
  UpdateStudentAssignmentGradeInput,
  GetAssignmentAndSubmissionBySubmissionIdDocument,
  GetProjectSubmissionDocument,
  UnifiedSubmission,
} from "../../../../graphql/generated/graphql";
import { useAuth } from "../../../../hooks/useAuth";
import { useNavigation } from "@react-navigation/native";
import type { StackNavigationProp } from "@react-navigation/stack";
import type { RootStackParamList } from "../../../../navigation/navigation.types";

type AdminGradingCenterNavProp = StackNavigationProp<RootStackParamList, "Admin Grading Center">;

interface GradingPanelProps {
  modalState: GradingCenterModalState;
  setModalState: (modalState: GradingCenterModalState) => void;
  setUpdateError: (updateError: string | null) => void;
  setActionResult: (actionResult: string) => void;
}

const GradingPanel: React.FC<GradingPanelProps> = ({ modalState, setModalState, setUpdateError, setActionResult }) => {
  const [slideAnim] = useState(new Animated.Value(0));
  const navigation = useNavigation<AdminGradingCenterNavProp>();

  const { userInfo } = useAuth();

  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    selectedSubmissionData,
    setSelectedSubmissionData,
    refetchUnifiedSubmissionsData,
    updatedAssessmentGradeEntry,
    updatedAssignmentPayload,
    updatedProjectPayload,
    setUpdatedAssessmentGradeEntry,
    setUpdatedAssignmentPayload,
    setUpdatedProjectPayload,
  } = useGradingCenterSubmissions();

  const handleCloseModal = () => {
    setModalState(GradingCenterModalState.CLOSED);
    setSelectedSubmissionData(null);
    navigation.setParams({ submissionId: undefined });
  };

  useEffect(() => {
    if (modalState !== GradingCenterModalState.CLOSED) {
      Animated.timing(slideAnim, {
        toValue: 1,
        duration: 300,
        useNativeDriver: false,
      }).start();
    } else {
      Animated.timing(slideAnim, {
        toValue: 0,
        duration: 300,
        useNativeDriver: false,
      }).start();
    }
  }, [modalState, slideAnim]);

  const handleBeforeUpdate = () => {
    setUpdateError(null);
    setIsSubmitting(true);
  };

  const handleAfterUpdate = (actionResultMessage: string, closeModal: boolean) => {
    setIsSubmitting(false);
    setActionResult(actionResultMessage);
    setUpdatedAssessmentGradeEntry(null);
    setUpdatedAssignmentPayload(null);
    setUpdatedProjectPayload(null);
    if (closeModal) {
      setModalState(GradingCenterModalState.CLOSED);
      setSelectedSubmissionData(null);
      navigation.setParams({ submissionId: undefined });
    }
  };

  const handleOnError = (errorMessage: string) => {
    setUpdateError(errorMessage);
    setIsSubmitting(false);
  };

  // ASSIGNMENT UPDATE
  const [updateStudentAssignmentGrade] = useUpdateStudentAssignmentGradeMutation({
    refetchQueries: [
      {
        query: GetGradingCenterDashboardDataDocument,
        variables: {
          facultyId: userInfo.userId,
        },
      },
      {
        query: GetAssignmentAndSubmissionBySubmissionIdDocument,
        variables: {
          submissionId: selectedSubmissionData?.id || "",
        },
      },
    ],
  });

  const handleSaveAssignmentGrade = async (
    submissionData: UnifiedSubmission,
    updatePayload: UpdateStudentAssignmentGradeInput
  ) => {
    if (updatePayload.grade === null || updatePayload.grade === undefined) {
      return;
    }

    handleBeforeUpdate();
    try {
      await updateStudentAssignmentGrade({
        variables: {
          submissionId: submissionData.id,
          updatePayload,
        },
      });

      await refetchUnifiedSubmissionsData();

      handleAfterUpdate(`Grade saved for ${submissionData.student?.name}`, true);
    } catch (error) {
      if (error?.message?.includes("Evaluation is currently pending")) {
        handleOnError(error.message);
      } else {
        handleOnError(error.message || "Failed to save grade");
      }
    }
  };

  const disableSave =
    isSubmitting ||
    (!updatedAssignmentPayload && !updatedAssessmentGradeEntry && !updatedProjectPayload) ||
    (updatedAssignmentPayload &&
      updatedAssignmentPayload.grade === selectedSubmissionData?.grade &&
      updatedAssignmentPayload.feedback === "" &&
      !updatedAssignmentPayload.retakeAllowed &&
      !updatedAssignmentPayload.retakeDeadline) ||
    (updatedProjectPayload &&
      updatedProjectPayload.grade === selectedSubmissionData?.grade &&
      updatedProjectPayload.feedback === "");

  // --- END OF ASSIGNMENT UPDATE ---

  // ASSESSMENT UPDATE

  const [updateAssessmentSubmissionGrade] = useUpdateAssessmentSubmissionGradeMutation({
    refetchQueries: [
      {
        query: GetGradingCenterDashboardDataDocument,
        variables: {
          facultyId: userInfo.userId,
        },
      },
      {
        query: GetAssessmentSubmissionsDetailsDocument,
        variables: {
          assessmentSubmissionId: selectedSubmissionData?.id || "",
        },
      },
      {
        query: GetAssessmentSubmissionAttemptHistoryDocument,
        variables: {
          assessmentSubmissionId: selectedSubmissionData?.id || "",
        },
      },
    ],
  });

  const handleSaveAssessmentGrade = async (
    submissionData: UnifiedSubmission,
    updatedGradeEntry: UpdateAssessmentSubmissionUpdatedGrade,
    closeModal: boolean
  ) => {
    handleBeforeUpdate();

    try {
      await updateAssessmentSubmissionGrade({
        variables: {
          assessmentSubmissionId: submissionData.id,
          updatedGradeEntry,
          reviewWithoutAdjustment: updatedGradeEntry.updatedEntityId ? false : true,
        },
      });

      await refetchUnifiedSubmissionsData();

      handleAfterUpdate(`Grade saved for ${submissionData.student?.name}`, closeModal);
    } catch (error) {
      if (error?.message?.includes("Evaluation is currently pending")) {
        handleOnError(error.message);
      } else {
        handleOnError(error.message || "Failed to save grade");
      }
    }
  };

  const handleSave = () => {
    if (modalState === GradingCenterModalState.VIEW_ASSESSMENT_RESPONSE) {
      if (!updatedAssessmentGradeEntry?.updatedEntityId) {
        setModalState(GradingCenterModalState.CLOSED);
        return;
      }
      if (updatedAssessmentGradeEntry) {
        handleSaveAssessmentGrade(selectedSubmissionData, updatedAssessmentGradeEntry, true);
      }
    }

    if (modalState === GradingCenterModalState.VIEW_ASSIGNMENT_RESPONSE) {
      if (!updatedAssignmentPayload) {
        setModalState(GradingCenterModalState.CLOSED);
        return;
      }
      handleSaveAssignmentGrade(selectedSubmissionData, updatedAssignmentPayload);
    }

    if (modalState === GradingCenterModalState.VIEW_PROJECT_RESPONSE) {
      if (!updatedProjectPayload) {
        setModalState(GradingCenterModalState.CLOSED);
        return;
      }
      handleSaveProjectGrade(selectedSubmissionData, updatedProjectPayload);
    }
  };

  // END OF ASSESSMENT UPDATE

  // PROJECT UPDATE
  const [gradeProjectSubmission] = useGradeProjectSubmissionMutation({
    refetchQueries: [
      {
        query: GetGradingCenterDashboardDataDocument,
        variables: {
          facultyId: userInfo.userId || "",
        },
      },
      {
        query: GetProjectSubmissionDocument,
        variables: {
          id: selectedSubmissionData?.id || "",
        },
      },
    ],
  });

  const handleSaveProjectGrade = async (
    submissionData: UnifiedSubmission,
    gradePayload: { grade: number | null; feedback: string }
  ) => {
    if (gradePayload.grade === null || gradePayload.grade === undefined) {
      return;
    }

    handleBeforeUpdate();
    try {
      await gradeProjectSubmission({
        variables: {
          submissionId: submissionData.id,
          grade: gradePayload.grade,
          feedback: gradePayload.feedback || undefined,
        },
      });

      await refetchUnifiedSubmissionsData();

      handleAfterUpdate(`Grade saved for ${submissionData.student?.name}`, true);
    } catch (error) {
      handleOnError(error.message || "Failed to save grade");
    }
  };

  // END OF PROJECT UPDATE

  const panelWidth = 800;
  const translateX = slideAnim.interpolate({
    inputRange: [0, 1],
    outputRange: [panelWidth, 0],
  });

  const panelContent = (
    <>
      {/* Dark overlay */}
      <Animated.View
        style={[
          styles.overlay,
          {
            opacity: slideAnim,
          },
        ]}
        onTouchEnd={handleCloseModal}
      />

      {/* Grading panel */}
      <Animated.View
        style={[
          styles.panel,
          {
            transform: [{ translateX }],
          },
        ]}>
        {/* Header */}
        <View style={styles.header}>
          <CustomText
            text={
              modalState === GradingCenterModalState.VIEW_ASSESSMENT_RESPONSE
                ? "Grade Assessment"
                : modalState === GradingCenterModalState.VIEW_PROJECT_RESPONSE
                  ? "Grade Project"
                  : "Grade Assignment"
            }
            size="l"
            weight="bold"
          />
          <CustomButton
            onPress={handleCloseModal}
            styleType="transparent"
            style={styles.closeButton}
            leftIcon={<XIcon size={24} color="#6B7280" />}
          />
        </View>

        {/* Conditional Panel Content */}
        {modalState === GradingCenterModalState.VIEW_ASSESSMENT_RESPONSE && (
          <AssessmentGradingPanel handleSaveAssessmentGrade={handleSaveAssessmentGrade} />
        )}
        {modalState === GradingCenterModalState.VIEW_ASSIGNMENT_RESPONSE && <AssignmentGradingPanel />}
        {modalState === GradingCenterModalState.VIEW_PROJECT_RESPONSE && <ProjectGradingPanel />}

        {/* Footer */}
        <View style={styles.footer}>
          <CustomButton
            text="Cancel"
            size="s"
            styleType="lightStroke"
            disabled={isSubmitting}
            onPress={handleCloseModal}
          />
          <CustomButton
            text="Save Grade"
            size="s"
            styleType="primary"
            disabled={disableSave}
            style={disableSave ? styles.disabledButton : {}}
            textStyle={disableSave ? styles.disabledText : {}}
            onPress={handleSave}
          />
        </View>
      </Animated.View>
    </>
  );

  if (modalState === GradingCenterModalState.CLOSED) return null;

  // Render to document body to ensure it's at the root level
  return createPortal(panelContent, document.body);
};

const styles = StyleSheet.create({
  disabledButton: {
    backgroundColor: "#E5E7EB",
    borderColor: "#D1D5DB",
    shadowColor: "transparent",
  },
  disabledText: {
    color: "#9CA3AF",
  },
  overlay: {
    // @ts-expect-error - works for web
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    zIndex: 99,
  },
  panel: {
    // @ts-expect-error - works for web
    position: "fixed",
    top: 0,
    right: 0,
    bottom: 0,
    width: 800,
    backgroundColor: "#FFFFFF",
    borderLeftWidth: 1,
    borderLeftColor: "#E5E7EB",
    shadowColor: "#000",
    shadowOffset: {
      width: -2,
      height: 0,
    },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 5,
    zIndex: 100,
    // @ts-expect-error - works for web
    height: "100vh",
    flexDirection: "column",
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingHorizontal: 24,
    paddingVertical: 20,
    backgroundColor: "#F9FAFB",
    borderBottomWidth: 1,
    borderBottomColor: "#EAECF0",
  },
  closeButton: {
    padding: 8,
  },
  footer: {
    flexDirection: "row",
    justifyContent: "space-between",
    gap: 12,
    padding: 24,
    backgroundColor: "#F9FAFB",
    borderTopWidth: 1,
    borderTopColor: "#EAECF0",
  },
});

export default GradingPanel;
